// Hashtag tagging + free-text search. The whole feature is client-side
// view state — nothing here flows to the server. Tags live inside
// `Task.label` and are extracted on demand; the active query is held
// in `local.query` and (optionally) mirrored to the URL fragment.

import { local, notify } from "./state";

/** Hashtag pattern. `\p{L}` accepts any Unicode letter (`é`, `中`, …);
 *  `\p{N}` any Unicode digit. A tag must:
 *   - follow start-of-string or whitespace (so URL fragments such as
 *     `foo.html#bar` don't accidentally tag),
 *   - start with a letter (no `#42`),
 *   - allow letters/digits/underscore/hyphen after.
 *  The capture group is the bare tag name, without the leading `#`. */
export const TAG_RE = /(?:^|\s)#(\p{L}[\p{L}\p{N}_-]*)/gu;

/** Returns deduplicated lowercased tag names found in a task label. */
export function extractTags(label: string): string[] {
  const out = new Set<string>();
  for (const m of label.matchAll(TAG_RE)) {
    out.add(m[1]!.toLowerCase());
  }
  return [...out];
}

export interface ParsedQuery {
  /** Tag names the task must carry (lowercased, AND across all). */
  tags: string[];
  /** Substrings the lowercased label must contain (AND across all). */
  text: string[];
  /** True when the query has no constraints — short-circuits matching. */
  empty: boolean;
}

/** Tokenize a free-form query string. `tag:foo` becomes a tag token;
 *  everything else is a case-insensitive substring constraint on the
 *  label. Empty tokens (`tag:`) are ignored. */
export function parseQuery(query: string): ParsedQuery {
  const tags: string[] = [];
  const text: string[] = [];
  for (const tok of query.split(/\s+/).filter(Boolean)) {
    if (tok.length >= 4 && tok.slice(0, 4).toLowerCase() === "tag:") {
      const name = tok.slice(4).toLowerCase();
      if (name) tags.push(name);
      // Bare `tag:` is dropped (treated as a misformed tag token, not text).
    } else {
      text.push(tok.toLowerCase());
    }
  }
  return { tags, text, empty: tags.length === 0 && text.length === 0 };
}

/** Test a label against a parsed query. AND across every constraint. */
export function matches(label: string, q: ParsedQuery): boolean {
  if (q.empty) return true;
  const lower = label.toLowerCase();
  for (const t of q.text) {
    if (!lower.includes(t)) return false;
  }
  if (q.tags.length > 0) {
    const tags = extractTags(label);
    for (const t of q.tags) {
      if (!tags.includes(t)) return false;
    }
  }
  return true;
}

/** Set the active query, mirror it to the URL hash, and notify
 *  subscribers. No-op when the value is unchanged. */
export function setQuery(q: string): void {
  if (local.query === q) return;
  local.query = q;
  writeHashQuery(q);
  notify();
}

/** Append `tag:<name>` to the current query if it's not already there
 *  (whole-token, case-insensitive match). Called when the user clicks
 *  a hashtag inside a task. */
export function appendTag(name: string): void {
  const norm = name.toLowerCase();
  const token = `tag:${norm}`;
  const cur = local.query;
  const present = cur
    .split(/\s+/)
    .some((t) => t.toLowerCase() === token);
  if (present) return;
  setQuery(cur ? `${cur} ${token}` : token);
}

// ── URL hash sync ──────────────────────────────────────────────────────
//
// We mirror the query into `location.hash` as `#q=<urlencoded>` so a
// filtered view is shareable and survives a page reload. The hash lives
// alongside the board path (`/<boardId>#q=…`) and never affects routing.

const HASH_KEY = "q";

export function readHashQuery(): string {
  const m = window.location.hash.match(/(?:^#|&)q=([^&]*)/);
  if (!m) return "";
  try {
    return decodeURIComponent(m[1]!);
  } catch {
    return "";
  }
}

/** Mirror a query into the URL hash without touching reactive state.
 *  The filter bar's keystroke path uses this directly (debounced) so
 *  typing never triggers a re-render. `setQuery` calls it too. */
export function writeHashQuery(q: string): void {
  const next = q ? `#${HASH_KEY}=${encodeURIComponent(q)}` : "";
  const url = `${window.location.pathname}${window.location.search}${next}`;
  // replaceState (not pushState) — typing into the filter shouldn't
  // pollute the back-button history with one entry per keystroke.
  window.history.replaceState(null, "", url);
}

/** Apply the URL hash's query (if any) to `local.query` and mount a
 *  `hashchange` listener so back/forward and manual edits stay in
 *  sync. Idempotent — call once on app boot. */
export function syncQueryFromHash(): void {
  local.query = readHashQuery();
  window.addEventListener("hashchange", () => {
    const next = readHashQuery();
    if (local.query !== next) {
      local.query = next;
      notify();
    }
  });
}
